import { useCallback, useEffect, useState } from 'react';

import { EMAIL_TEMPLATES, SENDER_EMAILS } from 'constants/email-config';
import { FilesFolders } from 'constants/files-folders-enum';
import { useApiUploadActions } from 'hooks/upload-actions';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { EditableSettings } from 'components/EditableSettings/EditableSettings';
import {
  Button,
  FilePicker,
  Input,
  InputGroup,
  RichTextEditor,
  Select,
  Textarea,
} from 'components/FormComponents';

export const AddNotificationModal = ({
  visible,
  companyId,
  modelParams,
  onSubmit = () => {},
  onClose = () => {},
}) => {
  const {
    register,
    watch,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const recipient = useWatch({ control, name: 'recipient' });

  const { signUpload, createUpload, deleteUpload, getUpload } = useApiUploadActions();

  const [templateKey, setTemplateKey] = useState('CUSTOM');
  const [senderEmailKey, setSenderEmailKey] = useState('ADMIN');

  const onUpdate = async data => {
    const payload = {
      ...data,
      sender: SENDER_EMAILS[senderEmailKey],
    };
    onSubmit(payload);
    reset();
  };

  const handleDocumentDelete = async id => {
    await deleteUpload(id);
  };

  const handleClose = useCallback(() => {
    reset();
    setTemplateKey('CUSTOM');
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    const selectedTemplate = EMAIL_TEMPLATES[templateKey];
    if (selectedTemplate) {
      setValue('subject', selectedTemplate.subject);
      setValue('message', selectedTemplate.message);
    }
  }, [templateKey, setValue]);

  useEffect(() => {
    if (recipient === 'user') {
      setTemplateKey('CUSTOM');
      setSenderEmailKey('ADMIN');
    }
  }, [recipient]);

  useEffect(() => {
    if (templateKey === 'ACIC_CANDIDATE') {
      setSenderEmailKey('ACIC_SUPPORT');
    } else {
      setSenderEmailKey('ADMIN');
    }
  }, [templateKey]);

  const availableTemplates = Object.entries(EMAIL_TEMPLATES).filter(
    ([key]) => !(recipient === 'user' && key === 'ACIC_CANDIDATE'),
  );

  const availableSenderEmails = Object.entries(SENDER_EMAILS).filter(
    ([key]) =>
      !(recipient === 'user' && key === 'ACIC_SUPPORT') &&
      !(templateKey !== 'ACIC_CANDIDATE' && key === 'ACIC_SUPPORT'),
  );

  return (
    <AnimatedModal visible={visible} onClose={handleClose} title="Send Email" icon="inbox" small>
      <form className="d-flex flex-column u-gap" onSubmit={handleSubmit(onUpdate)}>
        <InputGroup title="Recipient">
          <Select name="recipient" value={recipient} register={register} useDefault>
            <Select.Item value="candidate">Candidate</Select.Item>
            <Select.Item value="user">User</Select.Item>
          </Select>
        </InputGroup>

        <InputGroup title="Template">
          <Select value={templateKey} onChange={e => setTemplateKey(e.target.value)} useDefault>
            {(availableTemplates || []).map(([key, tpl]) => (
              <Select.Item key={key} value={key}>
                {tpl.label}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>

        <InputGroup title="Sender Email">
          <Select
            name="sender"
            register={register}
            value={senderEmailKey}
            onChange={e => setSenderEmailKey(e.target.value)}
            useDefault>
            {(availableSenderEmails || []).map(([key, value]) => (
              <Select.Item key={key} value={key}>
                {value}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>

        <InputGroup title="Subject">
          <Input name="subject" placeholder="Enter Subject.." register={register} />
        </InputGroup>

        <InputGroup title="Body">
          <Controller
            control={control}
            name="message"
            rules={{
              required: 'This field is required.',
              validate: value => {
                if (!value) return 'This field is required.';
                const plainText = value.replace(/<[^>]+>/g, '').trim();
                return plainText.length > 0 || 'This field is required.';
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <RichTextEditor
                onChange={onChange}
                value={value}
                error={error}
                allowFileUpload={false}
              />
            )}
          />
        </InputGroup>

        <InputGroup title="Add attachments">
          <Controller
            control={control}
            name="upload_id"
            render={({ field: { onChange, value } }) => (
              <FilePicker
                onChange={onChange}
                value={value}
                companyId={companyId}
                metadata={{
                  type: 'notification',
                  sender: 'admin',
                  recipient: recipient,
                  template: templateKey,
                }}
                modelParams={modelParams}
                bucketFolder={
                  templateKey === 'ACIC_CANDIDATE' ? FilesFolders.Acic : FilesFolders.Temp
                }
                getUpload={getUpload}
                signUpload={signUpload}
                createUpload={createUpload}
                deleteUpload={handleDocumentDelete}
              />
            )}
          />
        </InputGroup>

        <EditableSettings
          title="add a note"
          open={watch('add_note_active')}
          toggleable
          register={register}
          name="add_note_active">
          <InputGroup>
            <Textarea
              name="add_note"
              register={register}
              placeholder="Enter note..."
              inputProps={{ rows: 10 }}
            />
          </InputGroup>
        </EditableSettings>

        <Button submit>Send Email</Button>
      </form>
    </AnimatedModal>
  );
};
